import React, { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { LogOut, Trash2, Plus, RefreshCw, Mail } from "lucide-react";

const TABS = ["Blog", "Case Studies", "Testimonials", "Contacts"];

const AdminDashboard = () => {
    const { user, loading, logout } = useAuth();
    const [tab, setTab] = useState("Blog");
    const [posts, setPosts] = useState([]);
    const [cases, setCases] = useState([]);
    const [testis, setTestis] = useState([]);
    const [contacts, setContacts] = useState([]);
    const [editing, setEditing] = useState(null);

    const refresh = async () => {
        const [p, c, t, ct] = await Promise.all([
            api.get("/blog"),
            api.get("/case-studies"),
            api.get("/testimonials"),
            api.get("/admin/contacts").catch(() => ({ data: [] })),
        ]);
        setPosts(p.data); setCases(c.data); setTestis(t.data); setContacts(ct.data);
    };

    useEffect(() => { if (user) refresh(); }, [user]);

    if (loading) return <div className="pt-40 text-center">Loading…</div>;
    if (!user) return <Navigate to="/admin/login" replace />;

    const empty = {
        Blog: { slug: "", title: "", excerpt: "", content: "", cover_image: "", category: "Branding", author: "Sugandhi Luthra", published: true },
        "Case Studies": { slug: "", client: "", title: "", tagline: "", cover_image: "", services: [], industry: "", year: "2026", challenge: "", approach: "", outcome: "", metrics: [], gallery: [], featured: false, order: 100 },
        Testimonials: { quote: "", author: "", role: "", company: "", avatar: "", featured: true },
    };

    const startNew = () => setEditing({ ...empty[tab], _new: true });
    const saveEdit = async () => {
        if (!editing) return;
        const body = { ...editing }; delete body._new; delete body._id; const id = editing.id;
        try {
            if (tab === "Blog") {
                if (editing._new) await api.post("/admin/blog", body);
                else await api.put(`/admin/blog/${id}`, body);
            } else if (tab === "Case Studies") {
                if (editing._new) await api.post("/admin/case-studies", body);
                else await api.put(`/admin/case-studies/${id}`, body);
            } else if (tab === "Testimonials") {
                if (editing._new) await api.post("/admin/testimonials", body);
                else await api.put(`/admin/testimonials/${id}`, body);
            }
            setEditing(null);
            refresh();
        } catch (e) {
            const d = e.response?.data?.detail;
            alert(typeof d === "string" ? d : "Save failed");
        }
    };
    const removeItem = async (id) => {
        if (!window.confirm("Delete this item?")) return;
        try {
            if (tab === "Blog") await api.delete(`/admin/blog/${id}`);
            else if (tab === "Case Studies") await api.delete(`/admin/case-studies/${id}`);
            else if (tab === "Testimonials") await api.delete(`/admin/testimonials/${id}`);
            refresh();
        } catch (e) { /* ignore */ }
    };

    const list = tab === "Blog" ? posts : tab === "Case Studies" ? cases : tab === "Testimonials" ? testis : contacts;

    return (
        <main data-testid="admin-dashboard" className="min-h-screen bg-[var(--st-cream)]">
            <div className="border-b border-[var(--st-line)] sticky top-0 z-30 bg-[var(--st-cream)]/95 backdrop-blur">
                <div className="st-container py-5 flex flex-wrap items-center justify-between gap-4">
                    <div>
                        <p className="eyebrow text-[var(--st-gold-deep)]">SilverTree · Studio CMS</p>
                        <h1 className="font-serif text-3xl">Welcome back, {user.name?.split(" ")[0]}.</h1>
                    </div>
                    <div className="flex items-center gap-3">
                        <Link to="/" className="eyebrow link-underline">View site</Link>
                        <button onClick={refresh} className="btn-ghost py-2 px-3 text-xs"><RefreshCw size={14}/></button>
                        <button onClick={logout} data-testid="admin-logout" className="btn-ghost py-2 px-3 text-xs">
                            <LogOut size={14}/> Sign out
                        </button>
                    </div>
                </div>
                <div className="st-container flex gap-2 pb-4 overflow-x-auto no-scrollbar">
                    {TABS.map((t) => (
                        <button
                            key={t}
                            onClick={() => { setTab(t); setEditing(null); }}
                            data-testid={`admin-tab-${t.toLowerCase().replace(/ /g,'-')}`}
                            className={`px-4 py-2 text-xs uppercase tracking-widest border ${tab === t ? "bg-[var(--st-ink)] text-[var(--st-cream)]" : "border-[var(--st-line)]"}`}
                        >
                            {t}
                        </button>
                    ))}
                </div>
            </div>

            <div className="st-container py-10 grid grid-cols-1 lg:grid-cols-12 gap-10">
                <div className={editing ? "lg:col-span-5" : "lg:col-span-12"}>
                    {tab !== "Contacts" && (
                        <button onClick={startNew} data-testid="admin-new-item" className="btn-primary mb-6 whitespace-nowrap">
                            <Plus size={14}/> New {tab === "Case Studies" ? "case study" : tab.slice(0, -1).toLowerCase()}
                        </button>
                    )}
                    <div className="divide-y divide-[var(--st-line)] border-y border-[var(--st-line)]">
                        {list.length === 0 && <p className="py-8 text-[var(--st-ink-soft)]">No items yet.</p>}
                        {list.map((item) => (
                            <div key={item.id} className="py-4 flex items-center justify-between gap-4">
                                <div className="min-w-0">
                                    <p className="font-serif text-xl truncate">
                                        {tab === "Blog" ? item.title : tab === "Case Studies" ? item.client : tab === "Testimonials" ? item.author : item.name}
                                    </p>
                                    <p className="text-xs text-[var(--st-ink-soft)] truncate">
                                        {tab === "Blog" ? item.category : tab === "Case Studies" ? `${item.industry} · ${item.year}` : tab === "Testimonials" ? `${item.role}, ${item.company}` : (<><Mail size={10} className="inline mr-1"/>{item.email} · {item.interest || "—"}</>)}
                                    </p>
                                    {tab === "Contacts" && (
                                        <p className="text-sm mt-2 text-[var(--st-ink-soft)] line-clamp-2 italic">"{item.message}"</p>
                                    )}
                                </div>
                                {tab !== "Contacts" && (
                                    <div className="flex gap-2 shrink-0">
                                        <button onClick={() => setEditing({ ...item })} className="text-xs underline">Edit</button>
                                        <button onClick={() => removeItem(item.id)} className="text-xs text-red-700"><Trash2 size={14}/></button>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>

                {editing && tab !== "Contacts" && (
                    <div className="lg:col-span-7 border border-[var(--st-line)] p-6 bg-white/50">
                        <h2 className="font-serif text-3xl mb-4">{editing._new ? "Create" : "Edit"}</h2>
                        {tab === "Blog" && (
                            <>
                                <Input label="Title" v={editing.title} on={(v) => setEditing({ ...editing, title: v })} />
                                <Input label="Slug (unique-url-id)" v={editing.slug} on={(v) => setEditing({ ...editing, slug: v })} />
                                <Input label="Category" v={editing.category} on={(v) => setEditing({ ...editing, category: v })} />
                                <Input label="Cover image URL" v={editing.cover_image} on={(v) => setEditing({ ...editing, cover_image: v })} />
                                <Input label="Excerpt" v={editing.excerpt} on={(v) => setEditing({ ...editing, excerpt: v })} textarea />
                                <Input label="Content (markdown-lite, ## for subheadings)" v={editing.content} on={(v) => setEditing({ ...editing, content: v })} textarea rows={10}/>
                            </>
                        )}
                        {tab === "Case Studies" && (
                            <>
                                <Input label="Client" v={editing.client} on={(v) => setEditing({ ...editing, client: v })} />
                                <Input label="Slug" v={editing.slug} on={(v) => setEditing({ ...editing, slug: v })} />
                                <Input label="Title" v={editing.title} on={(v) => setEditing({ ...editing, title: v })} />
                                <Input label="Tagline" v={editing.tagline} on={(v) => setEditing({ ...editing, tagline: v })} />
                                <Input label="Cover image URL" v={editing.cover_image} on={(v) => setEditing({ ...editing, cover_image: v })} />
                                <Input label="Industry" v={editing.industry} on={(v) => setEditing({ ...editing, industry: v })} />
                                <Input label="Year" v={editing.year} on={(v) => setEditing({ ...editing, year: v })} />
                                <Input label="Services (comma-separated)" v={(editing.services || []).join(", ")} on={(v) => setEditing({ ...editing, services: v.split(",").map((s) => s.trim()).filter(Boolean) })} />
                                <Input label="Challenge" v={editing.challenge} on={(v) => setEditing({ ...editing, challenge: v })} textarea />
                                <Input label="Approach" v={editing.approach} on={(v) => setEditing({ ...editing, approach: v })} textarea />
                                <Input label="Outcome" v={editing.outcome} on={(v) => setEditing({ ...editing, outcome: v })} textarea />
                            </>
                        )}
                        {tab === "Testimonials" && (
                            <>
                                <Input label="Quote" v={editing.quote} on={(v) => setEditing({ ...editing, quote: v })} textarea />
                                <Input label="Author" v={editing.author} on={(v) => setEditing({ ...editing, author: v })} />
                                <Input label="Role" v={editing.role} on={(v) => setEditing({ ...editing, role: v })} />
                                <Input label="Company" v={editing.company} on={(v) => setEditing({ ...editing, company: v })} />
                                <Input label="Avatar URL" v={editing.avatar || ""} on={(v) => setEditing({ ...editing, avatar: v })} />
                            </>
                        )}
                        <div className="flex gap-3 mt-6">
                            <button onClick={saveEdit} data-testid="admin-save" className="btn-primary">Save</button>
                            <button onClick={() => setEditing(null)} className="btn-ghost">Cancel</button>
                        </div>
                    </div>
                )}
            </div>
        </main>
    );
};

const Input = ({ label, v, on, textarea, rows = 3 }) => (
    <div className="mb-4">
        <label className="eyebrow block mb-1">{label}</label>
        {textarea ? (
            <textarea className="st-input" rows={rows} value={v || ""} onChange={(e) => on(e.target.value)} />
        ) : (
            <input className="st-input" value={v || ""} onChange={(e) => on(e.target.value)} />
        )}
    </div>
);

export default AdminDashboard;
